import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import { downloadMISReport } from "@services/apiService";
import { useCSVExport } from "./useCSVExport";

/**
 * Extracts data array from various API response structures
 * @param {Object} responseData - API response data
 * @returns {Array} Extracted data array
 */
const extractDataFromResponse = (responseData, reportType) => {
  // claim-rejection report type
  if (reportType === "claim-rejection") {
    if (responseData.data && responseData.data.claims) {
      return responseData.data.claims;
    }
  }

  if (reportType === "active-journey") {
    if (responseData.data && responseData.data.active_journeys) {
      return responseData.data.active_journeys;
    }
  }

  if (reportType === "revenue") {
    if (responseData.data && responseData.data.revenue) {
      return responseData.data.revenue;
    }
  }

  if (responseData.items) return responseData.items;
  if (responseData.records) return responseData.records;
  if (responseData.results) return responseData.results;
  if (responseData.data && Array.isArray(responseData.data))
    return responseData.data;
  if (Array.isArray(responseData)) return responseData;

  // If none of the above, return empty array
  return [];
};

export const useMISReportDownload = ({
  reportType,
  reportName,
  fields,
  dataMapper,
  apiUrl,
  sheetName,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { exportToXLSX, generateDateFilename } = useCSVExport();

  const handleDownload = useCallback(
    async (additionalParams = {}) => {
      if (isDownloading) return;

      setIsDownloading(true);

      try {
        toast.info(`Fetching ${reportName} data...`, {
          toastId: `${reportType}-download-loading`,
        });

        const response = await downloadMISReport(
          reportType,
          {
            format: "xlsx",
            includeHeaders: true,
            ...additionalParams,
          },
          apiUrl,
        );

        if (response.success && response.data) {
          toast.dismiss(`${reportType}-download-loading`);
          // Extract data array from response using utility function
          const reportData = extractDataFromResponse(response.data, reportType);

          // Ensure we have an array of data
          if (!Array.isArray(reportData)) {
            throw new Error("Invalid data format received from API");
          }

          // Check if we have any data
          if (reportData.length === 0) {
            toast.warning(`No data available for ${reportName} report`);
            return { success: true, data: [], message: "No data available" };
          }

          const filename = generateDateFilename(`${reportType}-report`);

          exportToXLSX(
            reportData,
            fields,
            filename,
            dataMapper,
            sheetName || reportName,
          );

          return { success: true, data: reportData };
        } else {
          throw new Error(
            response.error || response.message || "Failed to fetch report data",
          );
        }
      } catch (error) {
        console.error("Download error:", error);
        toast.dismiss(`${reportType}-download-loading`);
        toast.error(
          error.message ||
            `Failed to download ${reportName} report. Please try again.`,
        );
        return { success: false, error: error.message };
      } finally {
        setIsDownloading(false);
      }
    },
    [
      isDownloading,
      reportType,
      reportName,
      fields,
      dataMapper,
      apiUrl,
      sheetName,
      exportToXLSX,
      generateDateFilename,
    ],
  );

  return {
    isDownloading,
    handleDownload,
    csvFields: fields,
    dataMapper,
  };
};

export default useMISReportDownload;
